// @ts-strict-ignore
import React, {useEffect, useRef, useState} from 'react';
import {create, InstanceProps} from 'react-modal-promise';
import clsx from 'clsx';

import {ModalReact, ModalType} from 'common/components/modal';
import {withStyles} from 'common/util/react';
import {IconSizeType} from 'common/constants';
import {useTranslation} from 'common/i18n/hooks';
import {DEFAULT_USER_NAME} from 'common/report/util/profile/types';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {reporter} from 'common/report/error-reporting';
import {getProfilePath} from 'common/report/util/profile/utils';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {LocalStorageKey} from 'common/report/constants';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {IronContextRegistry} from 'common/report/context-registry';
import {Visibility} from 'common/components/base/stackable';
import {ButtonTheme} from 'common/components/button/constants';
import {TableReact} from 'common/components/table';
import {InputReact} from 'common/components/input';
import {InputSize} from 'common/components/input/types';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {ButtonReact} from 'common/components/button';
import {useDomEvent} from 'common/hooks/useDomEvent';
import {getLocation} from 'common/util/url';
import {ReplayBanner} from 'common/widgets/replay-banner';
import {
  featureSamplingUtils,
  getSessionRecordingBlockingInfo,
  isServerSideStitchingEnabled,
} from 'common/report/util/feature-sampling';
import {getProjectId} from 'common/report/util';
import {CurrentProject} from 'common/report/model-resources/projects';
import {SwitchReact} from 'common/components/switch';
import {SvgIconReact} from 'common/components/svg-icon';
import {TooltipReact} from 'common/components/tooltip';

import {TrackedUserIconReact} from '../tracked-user-icon';
import {CopyToClipboardButtonReact} from '../copy-to-clipboard/copy-to-clipboard-button';
import {CopyType} from '../copy-to-clipboard/copy-to-clipboard-button/types';
import {promptFeedbackModal} from '../feedback-modal';
import {FeedbackForFeature, Sentiment} from '../feedback-modal/types';
import {DateRange} from '../date-range-picker/models/date-range';
import {getBannerTypeToDisplay, ReplayBannerType} from '../replay-banner/util';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import tableCss from './table.inline.styl';
import eventIndicatorCss from './event-indicator/index.module.styl?inline';
import {
  EmbedErrorStateType,
  RecordingEvents,
  RecordingMetadata,
  RecordingPlayerSource,
  RecordingPlayerTrackingEvent,
  ReplayErrorStateType,
} from './types';
import {EVENT_TIME_JUMP_OFFSET, RecordingPlayer} from './player';
import {DummyPlaceHolderReplayPlaylistRow, ReplayPlaylistRowEl} from './playlist-row';
import {ReplayPlaylistRowEventMetadataEl} from './playlist-row-events';
import {
  formatRecordingProgressTimeBar,
  getDisplayDate,
  getDisplayName,
  getDisplayTime,
  useReplayEvents,
  useReplayPageViewEvents,
} from './util';
import {ReplayPlaylistRowContextMenuReact} from './playlist-row-context-menu';

const PERSISTENCE_KEY = `replay-modal-persistence-key`;

type RecordingPlaylistPlayerProps = {
  replays: RecordingMetadata[];
  replayDateRange?: DateRange;
  open: boolean;
  onClose: () => void;
  source: RecordingPlayerSource;
  loadingReplays?: boolean; // Used to indicate that the replays are still being queried outside the component
  enableUpdateURL?: boolean; // Used to enable URL updating feature
  startingTimestamp?: string; // Used to start the replay at a specific timestamp
  startingReplayId?: string; // Used to start the playlist at a replay
  initialEvents?: RecordingEvents[]; // Used to pass in events if they are already queried
  extraTrackingProps?: Record<string, any>;
} & Pick<IronContextRegistry, 'localStorageManager'>;

type PersistedState = {
  seenReplayIds: string[];
};

enum SortOption {
  MostRecent = `MostRecent`,
  LeastRecent = `LeastRecent`,
  LongestDuration = `LongestDuration`,
  ShortestDuration = `ShortestDuration`,
  MostEvents = `MostEvents`,
  LeastEvents = `LeastEvents`,
}

const SortOptionLabels = {
  [SortOption.MostRecent]: ({t}) => t(`session-replay.sort.most-recent`),
  [SortOption.LeastRecent]: ({t}) => t(`session-replay.sort.least-recent`),
  [SortOption.LongestDuration]: ({t}) => t(`session-replay.sort.longest-duration`),
  [SortOption.ShortestDuration]: ({t}) => t(`session-replay.sort.shortest-duration`),
  [SortOption.MostEvents]: ({t}) => t(`session-replay.sort.most-events`),
  [SortOption.LeastEvents]: ({t}) => t(`session-replay.sort.least-events`),
};

function isSkipInactivityExperimentEnabled(currentProject?: CurrentProject) {
  const projectId = currentProject?.id.toString() || getProjectId();
  return featureSamplingUtils.determineFeatureEligibility(projectId, `session-replay-skip-inactivity`, 50);
}

export function RecordingPlaylistPlayer(props: RecordingPlaylistPlayerProps) {
  const startingReplayIdx = props.startingReplayId
    ? props.replays.findIndex((replay) => replay.replayId === props.startingReplayId)
    : 0;

  const [currentRecordingIdx, setCurrentRecordingIdx] = useState<number>(
    startingReplayIdx >= 0 ? startingReplayIdx : 0,
  );
  const [viewedRecordings, setViewedRecordings] = useState<Set<string>>(new Set());
  const [searchFilter, setSearchFilter] = useState<string>(``);
  const [selectedSortOption, setSelectedSortOption] = useState<SortOption>(SortOption.MostRecent);
  const [visibleRecordingsCount, setVisibleRecordingsCount] = useState<number>(props.replays.length);
  const [playlistCollapsed, setPlaylistCollapsed] = useState<boolean>(false);
  const [sortedReplays, setSortedReplays] = useState<RecordingMetadata[]>([]);
  const [currentRecordingProgressTime, setCurrentRecordingProgressTime] = useState<number>(0);
  const [copyRecordingProgressTime, setCopyRecordingProgressTime] = useState<number>(0);
  const [currentRecordingEventSelectedTime, setCurrentRecordingEventSelectedTime] = useState<number>(null);
  const [currentRecordingPaused, setCurrentRecordingPaused] = useState<boolean>(false);
  const [isStitchingEnabled, setIsStitchingEnabled] = useState<boolean>(true);

  const [expandedRowsReplayIds, setExpandedRowsReplayIds] = useState<Set<string>>(new Set());
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [replayBannerType, setReplayBannerType] = useState<ReplayBannerType>(ReplayBannerType.NoBanner);
  const [isSkipInactivityEnabled, setIsSkipInactivityEnabled] = useState<boolean>(false);
  const [currentContextMenuRecordingIdx, setCurrentContextMenuRecordingIdx] = useState<number | null>(null);

  const playlistTableRef = useRef<HTMLDivElement>(null);
  const currentSelectedRowRef = useRef<HTMLDivElement>(null);
  const playlistModalRef = useRef<HTMLDivElement>(null);
  const currentContextMenuRowRef = useRef<HTMLDivElement>(null);

  const currentProject = useCurrentProject();
  const enableSkipInactivity = isSkipInactivityExperimentEnabled(currentProject.get().value);
  const blockedReplayFrames = getSessionRecordingBlockingInfo(currentProject.get().value);

  const commonTrackingProps = {
    ...(props.extraTrackingProps || {}),
    banner_type: replayBannerType,
    blocked_replay_frames: blockedReplayFrames,
    is_fullscreen: isFullscreen,
    is_in_skip_inactivity_experiment: enableSkipInactivity,
    is_loading_replays: props.loadingReplays,
    is_playlist_collapsed: playlistCollapsed,
    is_server_side_stitching_enabled: isServerSideStitchingEnabled(currentProject.get().value),
    is_skip_inactivity_enabled: isSkipInactivityEnabled,
    num_played: viewedRecordings.size,
    num_replays_loaded: props.replays.length,
    num_replays_stitched: props.replays.filter((replay) => replay.isStitchedFromEvent).length,
    selected_sort_option: selectedSortOption,
    source: props.source,
  };

  const userLocalStorageHelper: UserLocalStorageHelper<PersistedState> = new UserLocalStorageHelper({
    context: props.localStorageManager,
    namespace: LocalStorageKey.SeenReplayIds,
  });
  const {t, print} = useTranslation();
  const {recordingsEventMetadataLoading, getReplayEventsForId, hasReplayPageViewEventsFromEventsQuery} =
    useReplayEvents(props.replays, sortedReplays[currentRecordingIdx], props.initialEvents || null);
  const {replayPageViewEventsLoaded, getReplayPageViewEventsForId, hasReplayPageViewEvents} = useReplayPageViewEvents(
    props.replays,
  );

  useEffect(() => {
    setViewedRecordings(getPlayedReplays());
    setTimeout(() => scrollCurrentRecordingToTop(), 200);
  }, []);

  useDomEvent(document, `fullscreenchange`, () => onFullscreenChange());

  useEffect(() => {
    const replaysCopy = [...props.replays];
    const _replayBannerType = getBannerTypeToDisplay(
      blockedReplayFrames,
      props.replayDateRange?.getCalendarDatesAsOf(),
    );
    setReplayBannerType(_replayBannerType);
    const newSortedReplays = sortReplaysBySortOption(replaysCopy);
    setSortedReplays(newSortedReplays);
    if (newSortedReplays.length > 0) {
      if (!props.startingTimestamp) {
        const targetTime =
          props.initialEvents?.find((entry) => entry.replayId === newSortedReplays[currentRecordingIdx].replayId)
            ?.replayEvents[0]?.eventTime || 0;

        if (targetTime) {
          setCurrentRecordingEventSelectedTime(targetTime - EVENT_TIME_JUMP_OFFSET);
        }
      } else {
        setCurrentRecordingEventSelectedTime(getTimestampFromReplaySeconds(props.startingTimestamp, newSortedReplays));
      }
    }
  }, [props.replays]);

  function getTimestampFromReplaySeconds(secondsInString: string, newSortedReplays: RecordingMetadata[]) {
    let secondsIn = 0;
    try {
      secondsIn = parseFloat(secondsInString);
    } catch {
      secondsIn = 0;
    }

    if (newSortedReplays.length > 0 && secondsIn) {
      return newSortedReplays[currentRecordingIdx].time + secondsIn;
    }
    return null;
  }

  function hasBeenPlayed(replayId: string): boolean {
    return viewedRecordings.has(replayId);
  }

  function updateSearchFilter(newValue: string) {
    setSearchFilter(newValue);
    if (!newValue) {
      setExpandedRowsReplayIds(new Set());
    } else {
      const allReplayIds = sortedReplays.map((replay) => replay.replayId);
      setExpandedRowsReplayIds(new Set(allReplayIds));
    }
  }

  function updateSortOption(newValue: SortOption) {
    setSelectedSortOption(newValue);
    if (sortedReplays.length > 0) {
      const newSortedReplays = sortReplaysBySortOption(sortedReplays, newValue);
      const newIndexOfCurrentReplay = newSortedReplays.findIndex(
        (replay) => replay.replayId === sortedReplays[currentRecordingIdx]?.replayId,
      );
      setSortedReplays(newSortedReplays);
      setCurrentRecordingIdx(newIndexOfCurrentReplay);
    }
  }

  function isSortOptionAscending(option: SortOption) {
    return (
      option === SortOption.LeastRecent || option === SortOption.ShortestDuration || option === SortOption.LeastEvents
    );
  }

  function sortReplaysBySortOption(currentReplays: RecordingMetadata[], currentSortOption = selectedSortOption) {
    const newSortedReplays = [...currentReplays];
    switch (currentSortOption) {
      case SortOption.MostRecent:
        return newSortedReplays.sort((a, b) => b.time - a.time);
      case SortOption.LeastRecent:
        return newSortedReplays.sort((a, b) => a.time - b.time);
      case SortOption.LongestDuration:
        return newSortedReplays.sort((a, b) => b.duration - a.duration);
      case SortOption.ShortestDuration:
        return newSortedReplays.sort((a, b) => a.duration - b.duration);
      case SortOption.MostEvents:
        return newSortedReplays.sort(
          (a, b) => getReplayEventsForId(b.replayId).length - getReplayEventsForId(a.replayId).length,
        );
      case SortOption.LeastEvents:
        return newSortedReplays.sort(
          (a, b) => getReplayEventsForId(a.replayId).length - getReplayEventsForId(b.replayId).length,
        );
    }
  }

  function persistPlayedReplay(replayId: string): void {
    const parsedValue = getPlayedReplays();
    try {
      const updatedValue = [...parsedValue, replayId];
      userLocalStorageHelper.setItem({
        key: PERSISTENCE_KEY,
        userId: userCookieUtils.getUserIdentityFromCookie()?.id,
        value: {seenReplayIds: updatedValue},
      });
      setViewedRecordings(new Set(updatedValue));
    } catch (err) {
      reporter.warn(err, `Unable to persist ${PERSISTENCE_KEY} to localStorage.`);
    }
  }

  function getPlayedReplays(): Set<string> {
    try {
      const persistedState = userLocalStorageHelper.getItem({
        key: PERSISTENCE_KEY,
        userId: userCookieUtils.getUserIdentityFromCookie()?.id,
      });
      return new Set(persistedState?.seenReplayIds) || new Set();
    } catch (err) {
      reporter.warn(err, `Unable to retrieve ${PERSISTENCE_KEY} value from localStorage.`);
      return new Set();
    }
  }

  function updateURL(distinctId: string, replayId: string | null, timestamp: number | null = null) {
    if (props.enableUpdateURL) {
      window.history.replaceState(null, null, getProfilePath(distinctId, null, replayId, timestamp));
    }
  }

  function findReplayIdx(replayId: string) {
    return sortedReplays.findIndex((replay) => replay.replayId === replayId);
  }

  function playNextRecording(replayId: string) {
    const replayIdx = findReplayIdx(replayId);
    const nextRecordingIdx = replayIdx + 1;

    if (!searchFilter && nextRecordingIdx < sortedReplays.length && nextRecordingIdx >= 0) {
      updateExpandedRows(sortedReplays[nextRecordingIdx].replayId);

      setCurrentRecordingIdx(nextRecordingIdx);
      setCurrentRecordingEventSelectedTime(null);

      setTimeout(() => scrollCurrentRecordingToTop(), 200);
    }
  }

  function updateExpandedRows(newExpandedReplayId: string, collapseCurrent = true) {
    const newExpandedRowsReplayIds = new Set(expandedRowsReplayIds);
    if (collapseCurrent && newExpandedRowsReplayIds.has(sortedReplays[currentRecordingIdx]?.replayId)) {
      newExpandedRowsReplayIds.delete(sortedReplays[currentRecordingIdx]?.replayId);
    }
    newExpandedRowsReplayIds.add(newExpandedReplayId);
    setExpandedRowsReplayIds(newExpandedRowsReplayIds);
  }

  function copyReplayLink(distinctId: string, replayId: string, includeTime = false) {
    const secondsIn = copyRecordingProgressTime - sortedReplays[currentRecordingIdx]?.time;
    return (
      getLocation().origin +
      getProfilePath(distinctId, null, replayId, includeTime ? Math.max(secondsIn, 0) : undefined)
    );
  }

  function jumpToEventTime(time: number, replayId: string) {
    if (replayId === sortedReplays[currentRecordingIdx]?.replayId) {
      setCurrentRecordingEventSelectedTime(time);
    } else {
      const replayIdx = findReplayIdx(replayId);
      setCurrentRecordingIdx(replayIdx);
      setCurrentRecordingEventSelectedTime(time);
    }
    // Clear the selected time after 1 second to ensure repeated jumping to the same event is possible
    setTimeout(() => setCurrentRecordingEventSelectedTime(null), 1000);
  }

  function onToggleExpand(replayId: string) {
    const newExpandedRowsReplayIds = new Set(expandedRowsReplayIds);
    if (newExpandedRowsReplayIds.has(replayId)) {
      newExpandedRowsReplayIds.delete(replayId);
    } else {
      newExpandedRowsReplayIds.add(replayId);
    }
    setExpandedRowsReplayIds(newExpandedRowsReplayIds);
  }

  function onFullscreenChange() {
    if (document.fullscreenElement) {
      setIsFullscreen(true);
    } else {
      setIsFullscreen(false);
    }
  }

  function onToggleFullscreen() {
    if (playlistModalRef.current) {
      if (!document.fullscreenElement && !isFullscreen) {
        playlistModalRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  }

  function scrollCurrentRecordingToTop() {
    if (playlistTableRef.current && currentSelectedRowRef.current) {
      const scrollDistance =
        currentSelectedRowRef.current.getBoundingClientRect().top -
        playlistTableRef.current.getBoundingClientRect().top;
      playlistTableRef.current.scrollBy({top: scrollDistance, behavior: `smooth`});
    }
  }

  function showFeedbackModal(sentiment: Sentiment) {
    promptFeedbackModal(
      {
        feature: FeedbackForFeature.SessionReplay,
        'negative-label': ``,
        placeholder: t(`session-replay.feedback-placeholder`),
        'sentiment-heading': t(`session-replay.feedback-description`),
        'positive-label': ``,
        title: t(`session-replay.feedback`),
      },
      {
        extraFeedbackEventName: RecordingPlayerTrackingEvent.Feedback,
        extraFeedbackEventProperties: {
          ...commonTrackingProps,
          replay_id: sortedReplays[currentRecordingIdx]?.replayId,
          replay_distinct_id: sortedReplays[currentRecordingIdx]?.distinctId,
        },
        initialSentiment: sentiment,
      },
    );
  }

  function getSortFromOption(option: SortOption) {
    switch (option) {
      case SortOption.MostRecent:
        return {field: `time`, order: -1};
      case SortOption.LeastRecent:
        return {field: `time`, order: 1};
      case SortOption.LongestDuration:
        return {field: `duration`, order: -1};
      case SortOption.ShortestDuration:
        return {field: `duration`, order: 1};
      case SortOption.MostEvents:
        return {field: `eventCount`, order: -1};
      case SortOption.LeastEvents:
        return {field: `eventCount`, order: 1};
      default:
        return {field: `time`, order: -1};
    }
  }

  function getClosedReplayAreaState(currentRecording: RecordingMetadata, displayName: string) {
    return (
      playlistCollapsed && (
        <div className={styles.closedPlaylistInfo}>
          <ButtonReact
            theme={ButtonTheme.Tertiary}
            icon='push-right'
            onClick={() => setPlaylistCollapsed(false)}
            tooltip={{text: t(`session-replay.expand-playlist`)}}
          />
          {currentRecording && (
            <>
              <a href={getProfilePath(currentRecording.distinctId, null)} target='_blank'>
                <TrackedUserIconReact
                  distinctId={currentRecording.distinctId}
                  imgSrc={currentRecording.avatarUrl}
                  email={currentRecording.email}
                  size={IconSizeType.Small}
                />
              </a>
              <div className={styles.closedPlaylistInfoText}>
                <div className={styles.closedPlaylistInfoTextTitle}>
                  {displayName !== DEFAULT_USER_NAME
                    ? displayName
                    : t(`session-replay.user-distinct-id`, {id: currentRecording.distinctId})}
                </div>
                {currentRecording.time && (
                  <div className={styles.closedPlaylistInfoTextSubtitle}>
                    {`${print(getDisplayDate(currentRecording.time, currentProject.get().value?.timezone))}, ${print(getDisplayTime(currentRecording.time, currentProject.get().value?.timezone))}`}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )
    );
  }

  function getErrorState(): ReplayErrorStateType | EmbedErrorStateType | null {
    let filteredReplays = sortedReplays;
    if (!isStitchingEnabled) {
      filteredReplays = filteredReplays.filter((replay) => !replay.isStitchedFromEvent);
    }

    if (!props.loadingReplays && !filteredReplays.length) {
      if (props.source === RecordingPlayerSource.UsersProfileLink) {
        return EmbedErrorStateType.NoReplayFound;
      } else {
        return ReplayErrorStateType.NoReplaysAvailable;
      }
    }
    return null;
  }

  function getReplayAreaState() {
    const currentRecording = sortedReplays[currentRecordingIdx];

    return (
      <>
        {currentRecording &&
          getClosedReplayAreaState(
            currentRecording,
            getDisplayName(currentRecording.email, currentRecording.name, currentRecording.distinctId),
          )}
        <div className={styles.feedbackContainer}>
          <ButtonReact
            theme={ButtonTheme.Tertiary}
            icon='thumbs-up'
            onClick={() => showFeedbackModal(Sentiment.Positive)}
            tooltip={{text: t(`session-replay.feedback-tooltip`)}}
          />
          <ButtonReact
            theme={ButtonTheme.Tertiary}
            icon='thumbs-down'
            onClick={() => showFeedbackModal(Sentiment.Negative)}
            tooltip={{text: t(`session-replay.feedback-tooltip`)}}
          />
        </div>
      </>
    );
  }

  function getPlaylist() {
    const sortInfo = getSortFromOption(selectedSortOption);
    let filteredReplays = sortedReplays;
    if (!isStitchingEnabled) {
      filteredReplays = filteredReplays.filter((replay) => !replay.isStitchedFromEvent);
    }

    return (
      <div className={`${styles.replaysListItemContainer}`} role={`rowgroup`} ref={playlistTableRef}>
        <TableReact
          emptyMessage={t(`session-replay.no-replays-found`)}
          loading={props.loadingReplays}
          defaultSortField={sortInfo.field}
          defaultSortOrder={sortInfo.order}
          searchFilter={searchFilter}
          className={styles.replaysListTable}
          rows={filteredReplays.map((replay) => ({
            ...replay,
            isExpanded: expandedRowsReplayIds.has(replay.replayId),
            eventCount: getReplayEventsForId(replay.replayId).length,
            replayEvents: getReplayEventsForId(replay.replayId),
            isSticky: replay.replayId === sortedReplays[currentRecordingIdx]?.replayId,
          }))}
          columns={[
            {
              field: `name`,
              flexGrow: `1`,
              cellRenderer: (replay: RecordingMetadata) =>
                ReplayPlaylistRowEl({
                  sel: `.${styles.replayPlaylistRowEl}`,
                  sortedReplays,
                  currentRecordingIdx,
                  currentlyPlaying:
                    replay.replayId === sortedReplays[currentRecordingIdx]?.replayId && !currentRecordingPaused,
                  hasBeenPlayed: hasBeenPlayed(replay.replayId),
                  replay,
                  replayEvents: getReplayEventsForId(replay.replayId) || [],
                  replayPageViewEventsLoaded,
                  replayPageViewEvents: getReplayPageViewEventsForId(replay.replayId) || [],
                  displayName: getDisplayName(replay.email, replay.name, replay.distinctId),
                  displayDate: print(getDisplayDate(replay.time, currentProject.get().value?.timezone)),
                  displayTime: print(getDisplayTime(replay.time, currentProject.get().value?.timezone)),
                  searchFilter,
                  isExpanded: expandedRowsReplayIds.has(replay.replayId),
                  onToggleExpand,
                  onTogglePlay: (replayId) => {
                    if (replayId === sortedReplays[currentRecordingIdx]?.replayId) {
                      setCurrentRecordingPaused(!currentRecordingPaused);
                    }
                  },
                  onHover: (hovered, currentTarget) => {
                    if (hovered) {
                      const currentIdx = findReplayIdx(replay.replayId);
                      setCurrentContextMenuRecordingIdx(currentIdx);
                      currentContextMenuRowRef.current = currentTarget;
                    } else {
                      setCurrentContextMenuRecordingIdx(null);
                    }
                  },
                  ref: replay.replayId === sortedReplays[currentRecordingIdx]?.replayId ? currentSelectedRowRef : null,
                  isStitchingEnabled,
                }),
              labelTransform: (row, _field) => {
                const eventNames = new Set(row.replayEvents.map((event) => event.eventName));
                const eventNamesString = Array.from(eventNames).join(` `);
                return `${getDisplayName(row.email, row.name, row.distinctId)} ${print(
                  getDisplayDate(row.time, currentProject.get().value?.timezone),
                )} ${print(
                  getDisplayTime(row.time, currentProject.get().value?.timezone),
                )} ${row.distinctId} ${eventNamesString}`;
              },
              isSearchable: true,
            },
          ]}
          rowDetailsRenderer={(replay: RecordingMetadata) => {
            return ReplayPlaylistRowEventMetadataEl({
              sel: `.${styles.replayPlaylistRowEventMetadataEl}`,
              loadingEventMetadata: recordingsEventMetadataLoading,
              replay,
              replayEvents: getReplayEventsForId(replay.replayId),
              jumpToEvent: jumpToEventTime,
              currentRecordingProgressTime:
                replay.replayId === sortedReplays[currentRecordingIdx]?.replayId ? currentRecordingProgressTime : null,
              searchFilter,
              isStitchingEnabled,
            });
          }}
          windowScroll={false}
          hideTitles
          onRowClick={({detail}) => {
            const replayIdx = findReplayIdx(detail.row.replayId as string);

            updateExpandedRows(detail.row.replayId);

            setCurrentRecordingIdx(replayIdx);
            setCurrentRecordingEventSelectedTime(null);
            setTimeout(() => scrollCurrentRecordingToTop(), 200);
          }}
          onNumResultsChange={({detail}) => {
            setVisibleRecordingsCount(detail.numResults);
          }}
          disableExpandableRows
          disableExpandableRowsIndent
          disableRowBackgroundHover
          stickyRowMode
          style-override={tableCss + eventIndicatorCss}
        />
      </div>
    );
  }

  const currentRecording = sortedReplays[currentRecordingIdx];
  let formattedRecordingProgress = `00:00`;
  if (sortedReplays.length > 0) {
    const formatRecordingProgressTimeBarResult = formatRecordingProgressTimeBar(
      (copyRecordingProgressTime - currentRecording.time) * 1000,
      currentRecording.duration,
    );
    formattedRecordingProgress = formatRecordingProgressTimeBarResult.formattedProgress;
  }

  let currentContextMenuRecording = null;
  if (currentContextMenuRecordingIdx !== null && sortedReplays.length > currentContextMenuRecordingIdx) {
    currentContextMenuRecording = sortedReplays[currentContextMenuRecordingIdx];
  }

  return (
    <div>
      <ModalReact
        modalType={ModalType.Modal}
        open={props.open}
        growToContent
        closeable
        elref='recordingPlayerModal'
        onChange={({detail}) => {
          if (detail.state === Visibility.Closed) {
            if (sortedReplays.length > 0) {
              updateURL(currentRecording.distinctId, null);
            }
            mixpanel.track(RecordingPlayerTrackingEvent.Close, {
              ...commonTrackingProps,
              replay_id: currentRecording?.replayId,
              replay_distinct_id: currentRecording?.distinctId,
            });
            props.onClose();
          } else if (detail.state === Visibility.Open) {
            if (sortedReplays.length > 0) {
              updateURL(currentRecording.distinctId, currentRecording.replayId);
            }
            mixpanel.track(RecordingPlayerTrackingEvent.Open, {
              ...commonTrackingProps,
              replay_id: currentRecording?.replayId,
              replay_distinct_id: currentRecording?.distinctId,
            });
          }
        }}
      >
        <div className={styles.recordingModal} ref={playlistModalRef}>
          {!playlistCollapsed && (
            <div className={`${styles.replaysList}`}>
              <div className={`${styles.replaysListHeader}`}>
                <div className={styles.replaysListHeaderText}>
                  <div className={styles.replaysListHeaderTitle}>{t(`session-replay.replays`)}</div>
                  {!props.loadingReplays && (
                    <div className={styles.replaysListHeaderSubtitle}>
                      {t(`session-replay.replays-count`, {number: visibleRecordingsCount})}
                    </div>
                  )}
                </div>
                <ButtonReact
                  theme={ButtonTheme.Tertiary}
                  icon='push-left'
                  onClick={() => setPlaylistCollapsed(true)}
                  notFocusable
                  tooltip={{text: t(`session-replay.collapse-playlist`)}}
                />
              </div>
              <div className={styles.replaysListControlsTop}>
                <InputReact
                  icon={`search`}
                  placeholder={t(`session-replay.search-placeholder`)}
                  size={InputSize.Medium}
                  value={searchFilter}
                  onInput={(ev) => updateSearchFilter(ev.target.value)}
                  showClear
                  onClear={() => updateSearchFilter(``)}
                />
                <SelectReact
                  theme={SelectTheme.Tertiary}
                  iconOnly
                  icon={{
                    leftIcon: isSortOptionAscending(selectedSortOption) ? `sort-value-asc` : `sort-value-desc`,
                  }}
                  menuWidth={150}
                  sections={[
                    {
                      items: Object.keys(SortOption).map((option) => ({
                        value: option,
                        label: print(SortOptionLabels[option]),
                      })),
                    },
                  ]}
                  options={Object.values(SortOption)}
                  onChange={(ev) => {
                    updateSortOption(ev.detail.item.value as SortOption);
                  }}
                  selectedOption={selectedSortOption}
                  tooltip={{text: t(`session-replay.sort-tooltip`)}}
                />
              </div>
              <div
                className={clsx({
                  [styles.replayBannerContainerDismissible]: replayBannerType === ReplayBannerType.Dismissible,
                  [styles.replayBannerContainerNonDismissible]: replayBannerType === ReplayBannerType.NonDismissible,
                })}
              >
                <div
                  className={clsx({
                    [styles.replayBannerNonDismissiblePosition]: replayBannerType === ReplayBannerType.NonDismissible,
                  })}
                >
                  <ReplayBanner bannerType={replayBannerType} />
                </div>
                {replayBannerType === ReplayBannerType.NonDismissible && (
                  <div className={styles.blurredRow}>
                    <DummyPlaceHolderReplayPlaylistRow />
                  </div>
                )}
              </div>
              {getPlaylist()}
              {isServerSideStitchingEnabled(currentProject.get().value) && (
                <div className={styles.replaysListControlsBottom}>
                  <div className={styles.enableStitching}>
                    <span>{t(`session-replay.enable-stitching`)}</span>
                    <div>
                      <SvgIconReact className={styles.infoIcon} icon='info' />
                      <TooltipReact
                        maxWidth={`256px`}
                        text={t('session-replay.enable-stitching-tooltip', {
                          docsLink: `https://docs.mixpanel.com/docs/session-replay/session-replay-web#server-side-stitching-beta`,
                        })}
                      />
                    </div>
                  </div>
                  <div>
                    <SwitchReact
                      on={isStitchingEnabled}
                      onChange={(ev) => {
                        const isEnabled = ev.detail.on;
                        setIsStitchingEnabled(isEnabled);

                        mixpanel.track(RecordingPlayerTrackingEvent.ToggleStitching, {
                          ...commonTrackingProps,
                          is_stitching_enabled: isEnabled,
                        });

                        if (!isEnabled && currentRecording?.isStitchedFromEvent) {
                          const nextObservedRecording = sortedReplays.find((replay) => !replay.isStitchedFromEvent);
                          if (nextObservedRecording) {
                            playNextRecording(nextObservedRecording.replayId);
                          } else {
                            setCurrentRecordingPaused(true);
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          <RecordingPlayer
            replay={currentRecording}
            replayEvents={getReplayEventsForId(currentRecording?.replayId)}
            isLoading={props.loadingReplays}
            onStartReplay={(replayId) => {
              const replayIdx = findReplayIdx(replayId);
              updateExpandedRows(replayId, false);

              updateURL(sortedReplays[replayIdx].distinctId, replayId);
              mixpanel.track(RecordingPlayerTrackingEvent.Watch, {
                ...commonTrackingProps,
                replay_id: replayId,
                replay_distinct_id: sortedReplays[replayIdx].distinctId,
              });
            }}
            onFinishReplay={(replayId) => {
              persistPlayedReplay(replayId);
              playNextRecording(replayId);
              mixpanel.track(RecordingPlayerTrackingEvent.Finish, {
                ...commonTrackingProps,
                replay_id: replayId,
                replay_distinct_id: sortedReplays[findReplayIdx(replayId)].distinctId,
              });
            }}
            onToggleFullscreen={() => onToggleFullscreen()}
            onToggleSkipInactivity={(on) => {
              setIsSkipInactivityEnabled(on);
              mixpanel.track(RecordingPlayerTrackingEvent.ToggleSkipInactivity, {
                ...commonTrackingProps,
                is_skip_inactivity_enabled: on,
              });
            }}
            onToggleCurrentRecordingPaused={(paused) => {
              setCurrentRecordingPaused(paused);
            }}
            isSkipInactivityEnabled={isSkipInactivityEnabled}
            showSkipInactiveSwitch={enableSkipInactivity}
            replayPageViewEventsLoaded={replayPageViewEventsLoaded}
            replayPageViewEvents={getReplayPageViewEventsForId(currentRecording?.replayId) || []}
            hasReplayPageViewEvents={hasReplayPageViewEvents() || hasReplayPageViewEventsFromEventsQuery()}
            updateCurrentReplayEvent={(time) => {
              setCurrentRecordingProgressTime(time);
            }}
            currentRecordingEventSelectedTime={currentRecordingEventSelectedTime}
            currentRecordingPaused={currentRecordingPaused}
            playlistCollapsed={playlistCollapsed}
            isFullscreen={isFullscreen}
            searchFilter={searchFilter}
            copyButton={
              sortedReplays.length !== 0 && (
                <CopyToClipboardButtonReact
                  className={styles.copyButton}
                  copyType={CopyType.Link}
                  onCopy={(ev) => {
                    mixpanel.track(RecordingPlayerTrackingEvent.CopyLink, {
                      ...commonTrackingProps,
                      replay_id: currentRecording.replayId,
                      replay_distinct_id: currentRecording.distinctId,
                      copied_url: ev.detail.value,
                    });
                  }}
                  options={[
                    {
                      value: copyReplayLink(currentRecording.distinctId, currentRecording.replayId),
                      label: t(`session-replay.copy-link`),
                    },
                    {
                      value: copyReplayLink(currentRecording.distinctId, currentRecording.replayId, true),
                      label: t(`session-replay.copy-link-position`, {position: formattedRecordingProgress}),
                    },
                  ]}
                  theme={ButtonTheme.Tertiary}
                  onClick={() => {
                    setCopyRecordingProgressTime(currentRecordingProgressTime);
                  }}
                />
              )
            }
            isStitchingEnabled={isStitchingEnabled}
            errorState={getErrorState()}
          >
            {getReplayAreaState()}
          </RecordingPlayer>
        </div>
        {currentContextMenuRecording !== null && (
          <ReplayPlaylistRowContextMenuReact
            contextPanelOpen={currentContextMenuRecording !== null}
            replay={currentContextMenuRecording}
            replayPageViewEvents={getReplayPageViewEventsForId(currentContextMenuRecording.replayId) || []}
            replayPageViewEventsLoaded={replayPageViewEventsLoaded}
            displayName={getDisplayName(
              currentContextMenuRecording.email,
              currentContextMenuRecording.name,
              currentContextMenuRecording.distinctId,
            )}
            sortedReplays={sortedReplays}
            currentRecordingIdx={currentRecordingIdx}
            positionParent={currentContextMenuRowRef?.current}
            onContextMenuHover={(currentIdx) => {
              setCurrentContextMenuRecordingIdx(currentIdx);
            }}
          />
        )}
      </ModalReact>
    </div>
  );
}

export const RecordingPlaylistPlayerStyled = withStyles(RecordingPlaylistPlayer, css);

export type RecordingPlayerModalTypes = {
  replays: RecordingMetadata[];
  replayDateRange?: DateRange;
  source: RecordingPlayerSource;
  loadingReplays?: boolean;
  emptyReplays?: boolean;
  enableUpdateURL?: boolean;
  startingTimestamp?: string;
  startingReplayId?: string;
  initialEvents?: RecordingEvents[];
  extraTrackingProps?: Record<string, any>;
};
type RecordingPlayerModalProps = RecordingPlayerModalTypes &
  InstanceProps<unknown> &
  Pick<IronContextRegistry, 'localStorageManager'>;

const RecordingPlayerModalWrapper: React.FC<RecordingPlayerModalProps> = (props: RecordingPlayerModalProps) => {
  return (
    <RecordingPlaylistPlayerStyled
      replays={props.replays}
      replayDateRange={props.replayDateRange}
      open={props.isOpen}
      onClose={props.onReject}
      source={props.source}
      loadingReplays={props.loadingReplays || false}
      enableUpdateURL={props.enableUpdateURL || false}
      startingTimestamp={props.startingTimestamp || null}
      startingReplayId={props.startingReplayId || null}
      initialEvents={props.initialEvents || []}
      localStorageManager={props.localStorageManager}
    />
  );
};

// Wrapper where you can pass in a promise to resolve the replays
type RecordingPlayerModalAsyncProps = Omit<
  RecordingPlayerModalProps,
  'replays' | 'loadingReplays' | 'initialEvents'
> & {
  eventsAndReplaysPromise: Promise<{replays: RecordingMetadata[]; events: RecordingEvents[]}>;
};
const RecordingPlayerModalAsyncWrapper: React.FC<RecordingPlayerModalAsyncProps> = (
  props: RecordingPlayerModalAsyncProps,
) => {
  const [replays, setReplays] = useState<RecordingMetadata[]>([]);
  const [loadingReplays, setLoadingReplays] = useState<boolean>(true);
  const [initialEvents, setInitialEvents] = useState<RecordingEvents[]>([]);
  const currentProject = useCurrentProject();
  const isSSSEnabled = isServerSideStitchingEnabled(currentProject.get().value);

  useEffect(() => {
    const startedAt = new Date().getTime();
    props.eventsAndReplaysPromise
      .then((loadedEventsAndReplays) => {
        setReplays(loadedEventsAndReplays.replays);
        setInitialEvents(loadedEventsAndReplays.events);
        setLoadingReplays(false);
        mixpanel.track(RecordingPlayerTrackingEvent.ListLoaded, {
          ...props.extraTrackingProps,
          source: props.source,
          is_server_side_stitching_enabled: isSSSEnabled,
          num_replays_loaded: loadedEventsAndReplays.replays.length,
          'Load time (ms)': new Date().getTime() - startedAt,
        });
      })
      .catch((err) => {
        reporter.error(err);
        setLoadingReplays(false);
        mixpanel.track(RecordingPlayerTrackingEvent.ListLoadFailed, {
          ...props.extraTrackingProps,
          source: props.source,
          is_server_side_stitching_enabled: isSSSEnabled,
          error: err,
          'Load time (ms)': new Date().getTime() - startedAt,
        });
      });
  }, []);

  return (
    <RecordingPlaylistPlayerStyled
      replays={replays}
      replayDateRange={props.replayDateRange}
      open={props.isOpen}
      onClose={props.onReject}
      source={props.source}
      loadingReplays={loadingReplays}
      enableUpdateURL={props.enableUpdateURL || false}
      startingTimestamp={props.startingTimestamp || null}
      startingReplayId={props.startingReplayId || null}
      initialEvents={initialEvents}
      localStorageManager={props.localStorageManager}
      extraTrackingProps={props.extraTrackingProps}
    />
  );
};

export const RecordingPlayerModal = create(RecordingPlayerModalWrapper);
export const RecordingPlayerModalAsync = create(RecordingPlayerModalAsyncWrapper);
